import { MutableRefObject, useState } from 'react';
import { CustomSchemaEntity, User } from '../../../../../../types';
import { Editor } from 'tinymce';
import {
  GroupHeader,
  GroupItems,
} from '../../../../../shared/autocomplete-groups-ui';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface AllFieldsProps {
  editorRef: MutableRefObject<Editor | null>;
  user?: User;
  customSchemas?: CustomSchemaEntity[];
  excludeSendAs?: boolean;
  excludeConditional?: boolean;
}

export default function AllFields({
  editorRef,
  user,
  customSchemas = [],
  excludeSendAs = false,
  excludeConditional = false,
}: AllFieldsProps) {
  const [selectedField, setSelectedField] = useState('User');
  const [selectedSubType, setSelectedSubType] = useState('First Name');
  const [selectedSubField, setSelectedSubField] = useState('All');
  const [gmailFieldBefore, setGmailFieldBefore] = useState('');
  const [gmailFieldAfter, setGmailFieldAfter] = useState('');
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [gmailFieldConditionalError, setGmailFieldConditonalError] =
    useState(false);

  function addCustomTypes(
    type:
      | 'emails'
      | 'phones'
      | 'addresses'
      | 'relations'
      | 'ims'
      | 'websites'
      | 'externalIds'
  ) {
    const customTypes =
      (user &&
        (user![type] as { customType: string }[])
          ?.filter((item) => item.customType)
          .map((item) => {
            return {
              name: item.customType,
              templateString: item.customType,
            };
          })) ||
      [];
    return customTypes;
  }

  const fieldObject = [
    ...(excludeSendAs
      ? []
      : [
          {
            type: 'gmail',
            name: 'Default Send As',
            templateString: 'DefaultSendAs',
          },
        ]),
    {
      type: 'user-profile',
      name: 'User',
      subType: [
        {
          name: 'First Name',
          templateString: 'first name',
        },
        {
          name: 'Last Name',
          templateString: 'last name',
        },
        {
          name: 'Profile Picture',
          templateString: 'profile picture',
        },
      ],
    },
    {
      type: 'user-profile',
      name: 'Email',
      subType: [
        {
          name: 'Primary',
          templateString: 'primary',
        },
        {
          name: 'Home',
          templateString: 'home',
        },
        {
          name: 'Work',
          templateString: 'work',
        },
        {
          name: 'Other',
          templateString: 'other',
        },
        {
          name: 'Custom',
          templateString: 'custom',
        },
        ...addCustomTypes('emails'),
      ],
    },
    {
      type: 'user-profile',
      name: 'Phone',
      subType: [
        {
          name: 'Home',
          templateString: 'home',
        },
        {
          name: 'Work',
          templateString: 'work',
        },
        {
          name: 'Mobile',
          templateString: 'mobile',
        },
        {
          name: 'Work Mobile',
          templateString: 'work_mobile',
        },
        {
          name: 'Home Fax',
          templateString: 'home_fax',
        },
        {
          name: 'Work Fax',
          templateString: 'work_fax',
        },
        {
          name: 'Pager',
          templateString: 'pager',
        },
        {
          name: 'Work Pager',
          templateString: 'work_pager',
        },
        {
          name: 'Main',
          templateString: 'main',
        },
        {
          name: 'Company Main',
          templateString: 'company_main',
        },
        {
          name: 'Assistant',
          templateString: 'assistant',
        },
        {
          name: 'Car',
          templateString: 'car',
        },
        {
          name: 'Radio',
          templateString: 'radio',
        },
        {
          name: 'ISDN',
          templateString: 'isdn',
        },
        {
          name: 'Callback',
          templateString: 'callback',
        },
        {
          name: 'TTY DD',
          templateString: 'tty_tdd',
        },
        {
          name: 'Google Voice',
          templateString: 'grand_central',
        },
        {
          name: 'Telex',
          templateString: 'telex',
        },
        {
          name: 'Other',
          templateString: 'other',
        },
        {
          name: 'Other Fax',
          templateString: 'other_fax',
        },
        {
          name: 'Custom',
          templateString: 'custom',
        },
        ...addCustomTypes('phones'),
      ],
    },
    {
      type: 'user-profile',
      name: 'IM',
      field: [
        {
          name: 'AOL Instant Messenger',
          templateString: 'aim',
        },
        {
          name: 'Google Talk',
          templateString: 'gtalk',
        },
        {
          name: 'ICQ',
          templateString: 'icq',
        },
        {
          name: 'Jabber',
          templateString: 'jabber',
        },
        {
          name: 'MSN Messenger',
          templateString: 'msn',
        },
        {
          name: 'Net Meeting',
          templateString: 'net_meeting',
        },
        {
          name: 'QQ',
          templateString: 'qq',
        },
        {
          name: 'Skype',
          templateString: 'skype',
        },
        {
          name: 'Yahoo Messenger',
          templateString: 'yahoo',
        },
        {
          name: 'Custom',
          templateString: 'custom_protocol',
        },
      ],
      subType: [
        {
          name: 'Home',
          templateString: 'home',
        },
        {
          name: 'Work',
          templateString: 'work',
        },
        {
          name: 'Other',
          templateString: 'other',
        },
        {
          name: 'Custom',
          templateString: 'custom',
        },
        ...addCustomTypes('ims'),
      ],
    },
    {
      type: 'user-profile',
      name: 'Address',
      field: [
        {
          name: 'All',
          templateString: 'all',
        },
        {
          name: 'All - Formatted',
          templateString: 'all_formatted',
        },
        {
          name: 'PO Box',
          templateString: 'pobox',
        },
        {
          name: 'Street Address',
          templateString: 'street',
        },
        {
          name: 'Extended Address',
          templateString: 'extended',
        },
        {
          name: 'City',
          templateString: 'city',
        },
        {
          name: 'State',
          templateString: 'state',
        },
        {
          name: 'Country',
          templateString: 'country',
        },
        {
          name: 'Postal Code',
          templateString: 'postal_code',
        },
      ],
      subType: [
        {
          name: 'Home',
          templateString: 'home',
        },
        {
          name: 'Work',
          templateString: 'work',
        },
        {
          name: 'Other',
          templateString: 'other',
        },
        {
          name: 'Custom',
          templateString: 'custom',
        },
        ...addCustomTypes('addresses'),
      ],
    },
    {
      type: 'user-profile',
      name: 'Relation',
      subType: [
        {
          name: 'Spouse',
          templateString: 'spouse',
        },
        {
          name: 'Child',
          templateString: 'child',
        },
        {
          name: 'Mother',
          templateString: 'mother',
        },
        {
          name: 'Father',
          templateString: 'father',
        },
        {
          name: 'Parent',
          templateString: 'parent',
        },
        {
          name: 'Brother',
          templateString: 'brother',
        },
        {
          name: 'Sister',
          templateString: 'sister',
        },
        {
          name: 'Friend',
          templateString: 'friend',
        },
        {
          name: 'Relative',
          templateString: 'relative',
        },
        {
          name: 'Domestic Partner',
          templateString: 'domestic_partner',
        },
        {
          name: 'Manager',
          templateString: 'manager',
        },
        {
          name: 'Assistant',
          templateString: 'assistant',
        },
        {
          name: 'Referred By',
          templateString: 'referred_by',
        },
        {
          name: 'Partner',
          templateString: 'partner',
        },
        {
          name: 'Custom',
          templateString: 'custom',
        },
        ...addCustomTypes('relations'),
      ],
    },
    {
      type: 'user-profile',
      name: 'Organization',
      field: [
        {
          name: 'All',
          templateString: 'all',
        },
        {
          name: 'Name',
          templateString: 'name',
        },
        {
          name: 'Title',
          templateString: 'title',
        },
        {
          name: 'Department',
          templateString: 'department',
        },
        {
          name: 'Location',
          templateString: 'location',
        },
        {
          name: 'Stock Symbol',
          templateString: 'stock_symbol',
        },
        {
          name: 'Cost Center',
          templateString: 'cost_center',
        },
        {
          name: 'Description',
          templateString: 'description',
        },
      ],
      subType: [
        {
          name: 'School',
          templateString: 'school',
        },

        {
          name: 'Work',
          templateString: 'work',
        },
        {
          name: 'Domain Only',
          templateString: 'domain_only',
        },
        {
          name: 'Unknown',
          templateString: 'unknown',
        },
      ],
    },
    {
      type: 'user-profile',
      name: 'External Id',
      subType: [
        {
          name: 'Account',
          templateString: 'account',
        },
        {
          name: 'Customer',
          templateString: 'customer',
        },
        {
          name: 'Network',
          templateString: 'network',
        },
        {
          name: 'Organization',
          templateString: 'organization',
        },
        {
          name: 'Custom',
          templateString: 'custom',
        },
        ...addCustomTypes('externalIds'),
      ],
    },
    {
      type: 'user-profile',
      name: 'Website',
      subType: [
        {
          name: 'App Install Page',
          templateString: 'app_install_page',
        },
        {
          name: 'Blog',
          templateString: 'blog',
        },
        {
          name: 'FTP',
          templateString: 'ftp',
        },
        {
          name: 'Home',
          templateString: 'home',
        },
        {
          name: 'Home Page',
          templateString: 'home_page',
        },
        {
          name: 'Other',
          templateString: 'other',
        },
        {
          name: 'Profile',
          templateString: 'profile',
        },
        {
          name: 'Reservations',
          templateString: 'reservations',
        },
        {
          name: 'Resume',
          templateString: 'resume',
        },
        {
          name: 'Work',
          templateString: 'work',
        },
        {
          name: 'Custom',
          templateString: 'custom',
        },
        ...addCustomTypes('websites'),
      ],
    },
    {
      type: 'user-profile',
      name: 'Gender',
      subType: [
        {
          name: 'Gender',
          templateString: 'gender',
        },
        {
          name: 'Pronouns',
          templateString: 'pronouns',
        },
      ],
    },
    ...(customSchemas?.length
      ? customSchemas.map((schema) => {
          return {
            type: 'custom-schemas',
            name: schema.schemaName,
            subType: schema.fields.map((field) => {
              return {
                name: field.fieldName,
                templateString: field.fieldName,
              };
            }),
          };
        })
      : []),
  ];

  const currentFieldIndex = fieldObject.findIndex(
    (field) => field.name === selectedField
  );
  const currentSubTypeIndex = fieldObject[currentFieldIndex].subType?.findIndex(
    (subField) => subField.name === selectedSubType
  );

  const currentSubFieldIndex = fieldObject[currentFieldIndex].field?.findIndex(
    (field) => field.name === selectedSubField
  );

  function getCurrentTemplateString() {
    // remove spaces from field name
    let templateString = fieldObject[currentFieldIndex].name.replace(/\s/g, '');

    // custom schemas operate differently
    if (fieldObject[currentFieldIndex].type === 'custom-schemas') {
      templateString =
        fieldObject[currentFieldIndex].subType![currentSubTypeIndex!]
          .templateString;
    } else {
      // has a subType
      if (fieldObject[currentFieldIndex].subType) {
        if (selectedSubType === 'Profile Picture' && width > 0 && height > 0) {
          templateString =
            templateString +
            '-' +
            selectedSubType +
            '-w' +
            width +
            '-h' +
            height;
        } else {
          templateString =
            templateString +
            '-' +
            fieldObject[currentFieldIndex].subType![currentSubTypeIndex!]
              .templateString;

          // has a subField
          if (fieldObject[currentFieldIndex].field) {
            templateString =
              templateString +
              '-' +
              fieldObject[currentFieldIndex].field![currentSubFieldIndex!]
                .templateString;
          }
        }
      }
    }

    return `{|${templateString}|}`;
  }

  return (
    <>
      <Stack flexDirection="row" gap={2} sx={{ mt: 3 }}>
        <Autocomplete
          id="grouped-values"
          autoComplete
          includeInputInList
          filterSelectedOptions
          openOnFocus
          autoHighlight
          selectOnFocus
          options={fieldObject}
          groupBy={(option) => option.type}
          renderGroup={(params) => (
            <li key={params.key}>
              <GroupHeader>
                {params.group === 'gmail'
                  ? 'Gmail Fields'
                  : params.group === 'user-profile'
                  ? 'User Profile Fields'
                  : 'Custom Schema Fields'}
              </GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
          getOptionLabel={(option) => option.name}
          sx={{ width: 200 }}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} label="Available Values" />
          )}
          value={
            fieldObject.find((field) => field.name === selectedField) ||
            fieldObject[0]
          }
          isOptionEqualToValue={(option, value) => {
            return option.name === value?.name;
          }}
          onChange={(_event, newValue) => {
            setSelectedField(newValue?.name || '');

            const currentFieldIndex = fieldObject.findIndex(
              (field) => field.name === newValue?.name
            );

            // Reset subType and subField
            if (fieldObject[currentFieldIndex].subType) {
              setSelectedSubType(
                fieldObject[currentFieldIndex].subType![0].name
              );
            } else {
              setSelectedSubType('');
            }

            if (fieldObject[currentFieldIndex].field) {
              setSelectedSubField(
                fieldObject[currentFieldIndex].field![0].name
              );
            } else {
              setSelectedSubField('');
            }
          }}
        />

        {fieldObject[currentFieldIndex].subType ? (
          <TextField
            inputProps={{
              id: `template-field-${selectedField}`,
            }}
            InputLabelProps={{
              htmlFor: `template-field-${selectedField}`,
            }}
            select
            label="Template Field"
            sx={{ width: 200 }}
            value={selectedSubType}
            onChange={(event) => {
              setSelectedSubType(event.target.value);
            }}
          >
            {fieldObject[currentFieldIndex].subType!.map((subField) => {
              return (
                <MenuItem value={subField.name} key={subField.name}>
                  {subField.name}
                </MenuItem>
              );
            })}
          </TextField>
        ) : null}
        {selectedSubType === 'Profile Picture' ? (
          <Stack flexDirection="row" gap={1}>
            <TextField
              label="width (px)"
              sx={{ width: 110 }}
              type="number"
              value={width}
              onChange={(e) => setWidth(parseInt(e.target.value))}
            />
            <TextField
              label="height (px)"
              sx={{ width: 110 }}
              type="number"
              value={height}
              onChange={(e) => setHeight(parseInt(e.target.value))}
            />
          </Stack>
        ) : null}
        {selectedField === 'Address' ||
        selectedField === 'IM' ||
        (selectedField === 'Organization' &&
          fieldObject[currentFieldIndex].field) ? (
          <TextField
            inputProps={{
              id: `field-${selectedSubField}`,
            }}
            InputLabelProps={{
              htmlFor: `field-${selectedSubField}`,
            }}
            select
            label="Field"
            sx={{ width: 200 }}
            value={selectedSubField}
            onChange={(event) => {
              setSelectedSubField(event.target.value);
            }}
          >
            {fieldObject[currentFieldIndex].field!.map((subField) => {
              return (
                <MenuItem value={subField.name} key={subField.name}>
                  {subField.name}
                </MenuItem>
              );
            })}
          </TextField>
        ) : null}
        <Button
          variant="contained"
          sx={{ height: 'fit-content' }}
          onClick={() => {
            if (editorRef.current) {
              editorRef.current.execCommand(
                'mceInsertContent',
                false,
                getCurrentTemplateString()
              );
            }
          }}
        >
          Add
        </Button>
      </Stack>
      {!excludeConditional && (
        <>
          <Typography sx={{ fontSize: 14, fontWeight: 300 }}>
            or Build Conditional
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <TextField
              label="Before Field"
              value={gmailFieldBefore}
              onChange={(event) => {
                setGmailFieldBefore(event.target.value);
              }}
              error={gmailFieldConditionalError}
              helperText={
                gmailFieldConditionalError ? 'You must fill in one.' : ' '
              }
              sx={{ width: 200 }}
            ></TextField>
            <TextField
              label="Template Field:"
              value={getCurrentTemplateString()}
              disabled
              sx={{ width: 200 }}
            ></TextField>
            <TextField
              label="After Field"
              value={gmailFieldAfter}
              onChange={(event) => {
                setGmailFieldAfter(event.target.value);
              }}
              error={gmailFieldConditionalError}
              helperText={
                gmailFieldConditionalError ? 'You must fill in one.' : ' '
              }
              sx={{ width: 200 }}
            ></TextField>
            <Button
              sx={{ height: 'fit-content' }}
              variant="contained"
              onClick={() => {
                if (
                  gmailFieldBefore.length === 0 &&
                  gmailFieldAfter.length === 0
                ) {
                  setGmailFieldConditonalError(true);
                } else {
                  setGmailFieldConditonalError(false);

                  let conditionString = getCurrentTemplateString() || '';

                  if (conditionString.length > 0) {
                    conditionString = conditionString.slice(2, -2);
                  }

                  conditionString = `${
                    gmailFieldBefore.length > 0 ? `[|${gmailFieldBefore}` : '[|'
                  }|${conditionString}|${
                    gmailFieldAfter.length > 0 ? `${gmailFieldAfter}|]` : '|]'
                  }`;

                  if (editorRef.current) {
                    editorRef.current.execCommand(
                      'mceInsertContent',
                      false,
                      conditionString
                    );
                  }
                }
              }}
            >
              Add
            </Button>
          </Stack>
        </>
      )}
    </>
  );
}
